import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Zap, ArrowRight, Cake, Weight, Ruler } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Onboarding() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [age, setAge] = useState("");
  const [weight, setWeight] = useState("");
  const [height, setHeight] = useState("");
  const [saving, setSaving] = useState(false);

  const finish = async () => {
    setSaving(true);
    try {
      const payload = {
        age: age === "" ? null : Number(age),
        weight: weight === "" ? null : Number(weight),
        height: height === "" ? null : Number(height),
      };
      const { data } = await api.put("/profile", payload);
      updateUser(data.user);
      localStorage.setItem("onboarding_done", "1");
      toast.success("Profil eingerichtet – los geht's!");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] px-6 pb-10 max-w-lg mx-auto flex flex-col pt-safe">
      <div className="flex items-center gap-2 mb-2">
        <Zap className="w-6 h-6 text-[#CCFF00]" fill="#CCFF00" />
        <span className="font-display font-black text-xl tracking-tighter">PACELINE</span>
      </div>
      <h1 className="font-display font-black text-4xl tracking-tighter leading-none mb-2">
        Willkommen, {user?.name?.split(" ")[0]}!
      </h1>
      <p className="text-neutral-400 text-sm mb-10">
        Ein paar Angaben für genaue Kalorien & Statistiken. Du kannst sie später im Profil ändern.
      </p>

      <div className="space-y-5" data-testid="onboarding-form">
        <div>
          <label className="data-label block mb-2">Alter</label>
          <div className="relative">
            <Cake className="w-5 h-5 text-[#CCFF00] absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              data-testid="onboarding-age"
              type="number"
              inputMode="numeric"
              value={age}
              onChange={(e) => setAge(e.target.value)}
              placeholder="z.B. 28"
              className="w-full bg-[#121212] border border-white/10 rounded-2xl pl-12 pr-14 py-4 text-white placeholder-neutral-600 focus:border-[#CCFF00] focus:outline-none transition-colors"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-500 text-sm font-bold">Jahre</span>
          </div>
        </div>

        <div>
          <label className="data-label block mb-2">Gewicht</label>
          <div className="relative">
            <Weight className="w-5 h-5 text-[#CCFF00] absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              data-testid="onboarding-weight"
              type="number"
              inputMode="decimal"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              placeholder="z.B. 70"
              className="w-full bg-[#121212] border border-white/10 rounded-2xl pl-12 pr-14 py-4 text-white placeholder-neutral-600 focus:border-[#CCFF00] focus:outline-none transition-colors"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-500 text-sm font-bold">kg</span>
          </div>
        </div>

        <div>
          <label className="data-label block mb-2">Größe</label>
          <div className="relative">
            <Ruler className="w-5 h-5 text-[#CCFF00] absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              data-testid="onboarding-height"
              type="number"
              inputMode="numeric"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              placeholder="z.B. 175"
              className="w-full bg-[#121212] border border-white/10 rounded-2xl pl-12 pr-14 py-4 text-white placeholder-neutral-600 focus:border-[#CCFF00] focus:outline-none transition-colors"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-500 text-sm font-bold">cm</span>
          </div>
        </div>
      </div>

      <div className="mt-auto pt-10 space-y-3">
        <button
          data-testid="onboarding-continue"
          onClick={finish}
          disabled={saving}
          className="w-full bg-[#CCFF00] text-black font-display font-black text-lg rounded-full py-4 flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60"
        >
          {saving ? "Speichern..." : "Weiter"} <ArrowRight className="w-5 h-5" />
        </button>
        <button
          data-testid="onboarding-skip"
          onClick={() => {
            localStorage.setItem("onboarding_done", "1");
            navigate("/", { replace: true });
          }}
          className="w-full text-neutral-500 font-semibold py-2 text-sm"
        >
          Später
        </button>
      </div>
    </div>
  );
}
